import { useEffect, useRef } from 'react';

import { CLUSTERS_FIELDS } from 'shared/constants/map';
import { useActivePinIdSelector } from 'shared/store/clientSide/slices/mapControls';
import { useGetClustersQuery } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/map/get-clusters/query';
import { clustersSelector } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/map/get-clusters/selectors';

import { useGetClusterByActivePinId } from './useGetClusterByActivePinId';
import { useUpdateChangedClusters } from './useUpdateChangedClusters';

/**
 * Хук следит за изменением активного пина. Примеры:
 * 1) был выбран пин, нажали на другой пин
 * 2) был выбран пин, закрыли листинг по пину
 * 3) был выбран пин, закрыли листинг по пину, нажали на другой пин
 * Его задача - выбрать текущий активный кластер, предыдущий активный кластер,
 * поменять их поля и отдать команду на обновление слоёв и менеджеров.
 */
export function useTrackActivePinChanged(): void {
  const activePinId = useActivePinIdSelector();
  const { data: clusters } = useGetClustersQuery({ select: clustersSelector });
  const activeCluster = useGetClusterByActivePinId();
  const updateChangedClusters = useUpdateChangedClusters();

  const previousActivePinId = useRef<string>();

  useEffect(() => {
    const deactivatedCluster = clusters?.find(item => item.id === previousActivePinId.current);

    if (deactivatedCluster) {
      delete deactivatedCluster.properties?.[CLUSTERS_FIELDS.isActive];
    }

    if (activeCluster) {
      activeCluster.properties ??= {};
      activeCluster.properties[CLUSTERS_FIELDS.isActive] = 1;
    }

    updateChangedClusters(activeCluster, deactivatedCluster);

    previousActivePinId.current = activePinId;
  }, [activePinId, activeCluster, clusters, updateChangedClusters, previousActivePinId]);
}
